import _ from "lodash";
import { readMirror, writeMirror } from "~/services/repos/baseRepo";

// Combo menu (a.k.a. "product set"). A parent SubItem can be bound to a recipe of
// child items. When the parent is ordered, the POS auto-inserts the children —
// each prefixed with "(W) ", priced 0, quantity = childQty * parentQty — so they
// appear on the table and print on the kitchen slip / preview receipt.
//
// Port of legacy `vm.product-set.js` (dataHolder + addSubComboList) and
// `vm.cart.js:checkAddCombo`. Phase 1 = ordering-time auto-insert only (no editor).

interface ComboChild {
  name: string;
  qty: number;
  subItemId: number;
}

// One DynamoDB `ProductSet` row: uuid = parent SubItem id (string).
interface ProductSetRow {
  customerId?: string;
  uuid: string | number;
  comboList: ComboChild[];
}

const MIRROR_RESOURCE = "productComboSet";

export const usePosProductComboSetStore = defineStore("productComboSet", () => {
  const loaded = ref(false);

  // parent subItemId (number) -> child recipe. Mirrors legacy `dataHolder`.
  const comboHolder = ref<Record<number, ComboChild[]>>({});

  const applyRows = (rows: ProductSetRow[]) => {
    const holder: Record<number, ComboChild[]> = {};
    _.forEach(rows, (row) => {
      const list = row?.comboList;
      if (Array.isArray(list) && list.length >= 1) {
        holder[Number(row.uuid)] = list;
      }
    });
    comboHolder.value = holder;
  };

  const getComboChildren = (subItemId: number | string): ComboChild[] | undefined => {
    if (subItemId == null) return undefined;
    return comboHolder.value[Number(subItemId)];
  };

  const hasCombo = (subItemId: number | string): boolean => {
    return !!getComboChildren(subItemId);
  };

  const isComboChildLine = (line: any): boolean => {
    if (line?.isComboChild) return true;
    const name = String(line?.name ?? "");
    return name.startsWith("(W) ") || name.startsWith(" (W) ");
  };

  // Shared expander — the single source of truth for both ordering entry points
  // (cart save via pos.cart._buildOrder, and mobile receive via AdapterMobileV1).
  // Walks `items` in order; after each combo parent, inserts its child lines.
  // Idempotent: a line that is itself a combo child is never expanded, so an
  // already-expanded array passes through unchanged (guards against the terminal
  // receive path double-inserting).
  const expandItems = (items: any[]): any[] => {
    if (!Array.isArray(items) || items.length === 0) return items;

    // FULL BYPASS for shops with no combo recipes — the vast majority during the
    // transition. Returns the SAME array reference untouched. Also covers the
    // case where the ProductSet fetch returned null / failed (holder stays {}),
    // so no combo data === behaves exactly like before this feature existed.
    if (Object.keys(comboHolder.value).length === 0) return items;

    const result: any[] = [];
    let major = 0;

    _.forEach(items, (line) => {
      result.push(line);

      if (isComboChildLine(line)) return;

      const subItemId = line?.subItemId ?? line?.subitemid;
      const children = getComboChildren(subItemId);
      if (!children) return;

      major++;
      const parentQty = Number(line?.quantity ?? line?.qty ?? 1) || 1;

      _.forEach(children, (child) => {
        const childQty = (Number(child.qty) || 1) * parentQty;
        result.push({
          name: " (W) " + child.name,
          price: 0,
          extPrice: 0,
          quantity: childQty,
          qty: childQty,
          subItemId: child.subItemId,
          subitemid: child.subItemId,
          toppings: [],
          comments: "",
          uuid: _.random(1, 100000) + ":" + Date.now(),
          isComboChild: true,
          majorSet: major,
          minorSet: major,
        });
      });
    });

    return result;
  };

  // Cache-first (IDB mirror), same pattern as the DynamoDB-backed settings.
  // ProductSet isn't version-tracked; it changes rarely and is edited elsewhere.
  // Force a fresh fetch from the cloud, then rebuild the holder + IDB mirror.
  // Used by the combo editor after a save/remove and by finishLoad's cold path.
  const fetchAndApply = async () => {
    const fresh = ((await useDataservice().productComboSet.fetch()) || []) as ProductSetRow[];
    await writeMirror(MIRROR_RESOURCE, fresh);
    applyRows(fresh);
    return fresh;
  };

  const finishLoad = async () => {
    const cached = await readMirror<ProductSetRow[]>(MIRROR_RESOURCE);
    if (cached != null) {
      applyRows(cached);
      loaded.value = true;
      return;
    }

    try {
      await fetchAndApply();
    } catch (err) {
      console.warn("[productComboSet] fetch failed", err);
      applyRows([]);
    }
    loaded.value = true;
  };

  const refresh = async () => {
    try {
      await fetchAndApply();
    } catch (err) {
      console.warn("[productComboSet] refresh failed", err);
    }
  };

  // Editor: upsert one combo recipe. The DynamoDB row's sortKey (uuid) is the
  // parent SubItem id; POST is an unconditional PutItem (create-or-overwrite),
  // matching legacy vm.product-set.saveBatch. Empty comboList removes the combo.
  const saveCombo = async (parentSubItemId: number | string, comboList: ComboChild[]) => {
    const storeId = usePosShopStore().storeId;
    const uuid = String(parentSubItemId);

    if (!comboList || comboList.length === 0) {
      return removeCombo(parentSubItemId);
    }

    const payload = {
      customerId: storeId,
      uuid,
      comboList: comboList.map((c) => ({
        name: c.name,
        qty: Number(c.qty) || 1,
        subItemId: Number(c.subItemId),
      })),
    };
    await useDataservice().productComboSet.postByDynamoFormat(storeId, payload);
    await refresh();
  };

  // Editor: delete one combo recipe by parent SubItem id.
  const removeCombo = async (parentSubItemId: number | string) => {
    const storeId = usePosShopStore().storeId;
    await useDataservice().productComboSet.removeByDynamoFormat(storeId, String(parentSubItemId));
    await refresh();
  };

  return {
    loaded,
    comboHolder,
    getComboChildren,
    hasCombo,
    expandItems,
    finishLoad,
    refresh,
    saveCombo,
    removeCombo,
  };
});
